import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Fab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  Assignment,
  DriveFileMoveOutlined,
  Edit,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined,
  RemoveCircle,
  UndoRounded,
} from '@mui/icons-material'
import { Form, Formik } from 'formik'
import * as React from 'react'
import { inject } from 'mobx-react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import CardWithTitle from '../common/CardWithTitle/CardWithTitle'
import FullWidthField from '../common/FullWidthField'
import { IProviderTypeDto } from '../generated_client'
import * as Yup from 'yup'
import QuoteDetails from './QuoteDetails'
import { createIQuoteFormValues, submitQuoteFormValues } from './QuoteFormValues'
import QuoteTotals from './QuoteTotals'
import { IStores } from 'src/Stores'
import { formatCurrency } from 'src/utils/Formatter'
import AddQuoteLineModal from './AddQuoteLineModal'
import EditQuoteLineModal from './EditQuoteLineModal'
import { IQuoteDTO } from 'src/viewModels/quotes/QuoteDTO'
import { QuoteStatus } from 'src/utils/QuoteStatus'
import { CostSourceText } from './CostSourceText'
import { QuoteLineItemType } from 'src/utils/QuoteLineItemType'
import { QuoteInboxItem } from 'src/viewModels/workflow/QuoteInboxItem'
import {
  GetAnesthesiaLineItems,
  GetMiscellaneousLineItems,
  GetPhysicalTherapyLineItems,
  GetProfessionalComponentLineItems,
  GetTechnicalComponentLineItems,
  GetClaimButtonsWithSummary,
} from 'src/services/QuoteService'

// Add strikeout style to the document
if (typeof document !== 'undefined') {
  const styleId = 'quote-strikeout-style'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = '.strike-out-text { text-decoration: line-through red; }'
    document.head.appendChild(style)
  }
}

const QuoteSchema = Yup.object().shape({
  adjustedCost: Yup.number()
    .transform(function (value, _originalValue) {
      return this.isType(value) ? value : -1
    })
    .min(0, 'Adjusted Cost must be 0 or greater')
    .max(1000000000000, 'Adjusted Cost must be 1000000000000 or less'),
  targetMargin: Yup.number()
    .transform(function (value, _originalValue) {
      return this.isType(value) ? value : -1
    })
    .min(0, 'Target Margin must be 0% or greater')
    .max(100, 'Target Margin must be 100% or less'),
})

interface ICreateQuoteRouteParams {
  quoteId?: string
}

interface ICreateQuoteProps {
  match?: {
    params: ICreateQuoteRouteParams
  }
  history?: any
  location?: any
  getLineItemsTotals?: (lineItems: any) => number
  getProviderTypes?: () => Promise<void>
  getTypesByCategory?: (type: string) => Promise<void>
  isContractModalOpen?: boolean
  isMultpleProcedureDiscountDialogOpen?: boolean
  loadQuote?: (quoteId: string, reload?: boolean) => Promise<void>
  providerTypes?: IProviderTypeDto[]
  rerouteToPath?: (path: string) => void
  selectedQuote?: IQuoteDTO
  openModal?: (quote: IQuoteDTO, modalSwitch: string) => void
  openEditModal?: (lineItem: any, lineItemIndex: number, lineItemType: string) => void
  assistantWarning?: boolean
  isAdjusterEmailModalOpen?: boolean
  setAsViewOnly?: boolean
  isNum?: (str: any) => boolean
  deleteLineItem?: (
    property: QuoteLineItemType,
    index: number,
    reconcile: boolean,
    forceUpdateHandler: () => void
  ) => void
  excludeAllLineItems?: (forceUpdateHandler: () => void) => void
  deleteAllLineItems?: (reconcile: boolean, forceUpdateHandler: () => void) => void
  getSubtotal?: (property: QuoteLineItemType) => string
  renderLegend?: () => JSX.Element
  setGetQuoteValuesForStaticAction?: (parentfunction?: () => Promise<IQuoteDTO>) => void
  renderClaimButtons?: (section: string) => JSX.Element | JSX.Element[]
  quoteInboxItem: QuoteInboxItem
  isReferralContactModalOpen?: boolean
  openReferralContactModal?: () => void
  openBundleClaimsModal?: () => void
}

interface ICreateQuoteState {
  reload: boolean
  generatedLink: string
  expandProfessional: boolean
  expandTechnical: boolean
  expandAnesth: boolean
  expandPhysical: boolean
  expandMisc: boolean
  lineItemDirty: boolean
  pcListStringify: string
  tcListStringify: string
  anListStringify: string
  ptListStringify: string
  miListStringify: string
  isLoading: boolean
  viewOnly: boolean
  hasNotSubmitted: boolean
  buttonsLoading: boolean
}

class CreateQuote extends React.Component<ICreateQuoteProps, ICreateQuoteState> {
  constructor(props: ICreateQuoteProps) {
    super(props)
    this.state = {
      reload: false,
      generatedLink: '',
      expandProfessional: true,
      expandTechnical: true,
      expandAnesth: true,
      expandPhysical: true,
      expandMisc: true,
      lineItemDirty: false,
      pcListStringify: '',
      tcListStringify: '',
      anListStringify: '',
      ptListStringify: '',
      miListStringify: '',
      isLoading: true,
      viewOnly: false,
      hasNotSubmitted: true,
      buttonsLoading: true,
    }
    this.forceUpdateHandler = this.forceUpdateHandler.bind(this)
  }

  private wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

  private async waitForQuoteRef(maxAttempts = 10, delayMs = 100) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const hasQuoteRef = !!this.quoteRef.current
      const hasValues = !!this.quoteRef.current?.values

      if (hasQuoteRef && hasValues) {
        return
      }

      await this.wait(delayMs)
    }
  }

  public setMode() {
    var status = this.props.selectedQuote?.status

    if (this.props.setAsViewOnly) {
      this.setState({ viewOnly: true })
    } else {
      if (
        status == QuoteStatus.Imported ||
        status == QuoteStatus.Quoted ||
        status == QuoteStatus.Dismissed
      ) {
        this.setState({ viewOnly: true })
      }
    }
  }

  public forceUpdateHandler() {
    var newPC = JSON.stringify(
      this.props.selectedQuote?.professionalComponentLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )
    var newTC = JSON.stringify(
      this.props.selectedQuote?.technicalComponentLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )
    var newAN = JSON.stringify(
      this.props.selectedQuote?.anesthesiaLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )
    var newPT = JSON.stringify(
      this.props.selectedQuote?.physicalTherapyLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )
    var newMI = JSON.stringify(
      this.props.selectedQuote?.miscellaneousLineItems!.filter(
        (x) => x.id != undefined || (x.id == undefined && x.markedDeleted != true)
      )
    )

    this.setState({
      lineItemDirty:
        this.state.pcListStringify != newPC ||
        this.state.tcListStringify != newTC ||
        this.state.anListStringify != newAN ||
        this.state.ptListStringify != newPT ||
        this.state.miListStringify != newMI,
    })

    this.setMode()
    this.forceUpdate()
  }

  public async componentDidMount() {
    this.props.setGetQuoteValuesForStaticAction!(this.staticActionSave)
    await this.setup()
  }

  public componentDidUpdate() {
    // Always ensure the callback is set to handle navigation scenarios
    // This fixes the issue where navigating back from CopyQuote leaves the reference undefined
    this.props.setGetQuoteValuesForStaticAction!(this.staticActionSave)
  }

  public componentWillUnmount() {
    this.props.setGetQuoteValuesForStaticAction!(undefined)
    this.setState({ hasNotSubmitted: true })
    // Clear the store's reference to prevent stale refs when navigating back
    this.props.setGetQuoteValuesForStaticAction!(undefined)
  }

  public getLineItemStyle = (deleted: boolean) => {
    if (deleted) {
      return 'strike-out-text'
    } else {
      return undefined
    }
  }

  public async setup() {
    const { getProviderTypes, getTypesByCategory } = this.props

    await Promise.all([
      getProviderTypes!(),
      getTypesByCategory!('PC'),
      getTypesByCategory!('TC'),
      getTypesByCategory!('MI'),
      this.fetchAnesthesiaLineItems(),
      this.fetchMiscellaneousLineItems(),
      this.fetchPhysicalLineItems(),
      this.fetchProfessionalLineItems(),
      this.fetchTechnicalLineItems(),
      this.fetchAnesthesiaClaimButtons(),
      this.fetchMiscellaneousClaimButtons(),
      this.fetchPhysicalClaimButtons(),
      this.fetchProfessionalClaimButtons(),
      this.fetchTechnicalClaimButtons(),
    ]).then(() => {
      this.setState(
        {
          lineItemDirty: false,
          pcListStringify: JSON.stringify(
            this.props.selectedQuote!.professionalComponentLineItems
          ),
          tcListStringify: JSON.stringify(
            this.props.selectedQuote!.technicalComponentLineItems
          ),
          anListStringify: JSON.stringify(this.props.selectedQuote!.anesthesiaLineItems),
          ptListStringify: JSON.stringify(
            this.props.selectedQuote!.physicalTherapyLineItems
          ),
          miListStringify: JSON.stringify(
            this.props.selectedQuote!.miscellaneousLineItems
          ),
          isLoading: false,
        },
        () => {
          this.forceUpdateHandler()
        }
      )
    })
  }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload, isLoading: true }, () => {
      submitForm()
    })
  }

  public get title() {
    var title = 'Add New Quote'
    if (this.props.match?.params.quoteId) {
      if (this.state.viewOnly) {
        title = 'View Quote'
      } else {
        title = 'Edit Quote'
      }
    }

    return title
  }

  public staticActionSave = async () => {
    await this.waitForQuoteRef()

    if (!this.quoteRef.current) {
      throw new Error('Quote form reference is not available')
    }

    let values = this.quoteRef.current.values
    this.quoteRef.current.submitForm()

    const quote = await submitQuoteFormValues(values, false).finally(() => {
      this.setState({ hasNotSubmitted: false }, () => {
        if (this.quoteRef.current) {
          this.quoteRef.current.setSubmitting(false)
        }
      })
    })

    return quote
  }

  fetchAnesthesiaLineItems = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}

      GetAnesthesiaLineItems(quoteId!).then((result) => {
        this.props.selectedQuote!.anesthesiaLineItems! = result
        resolve(result)
      })
    })
  }

  fetchAnesthesiaClaimButtons = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'AN'

      GetClaimButtonsWithSummary(quoteId!, providerType).then((result) => {
        this.props.selectedQuote!.claimButtonsAN = result.claimButtons
        this.props.selectedQuote!.claimButtonsANTotals = result.providerTypeSummaries
        this.setState({ buttonsLoading: false })
        resolve(result)
      })
    })
  }

  private mapAnesthesiaGridData = () => {
    if (this.props.selectedQuote?.anesthesiaLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={15} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.anesthesiaLineItems!.map((billOut, index) => (
        <TableRow key={index} className="gridPadding">
          <TableCell>
            {this.props.selectedQuote?.isConsumer &&
            billOut.movedToQuoteLegacyEOCGroupNumber ? (
              <Tooltip title={`Consumed Line Item`}>
                <span
                  style={{
                    fontSize: 'large',
                    verticalAlign: 'middle',
                    display: 'inline-flex',
                    alignItems: 'center',
                    paddingBottom: '8px',
                    cursor: 'default',
                  }}
                >
                  🐠&nbsp;&nbsp;
                </span>
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            style={{ whiteSpace: 'nowrap' }}
          >
            {billOut.providerName}
          </TableCell>
          <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
            {billOut.physicianName}
          </TableCell>
          <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
            {billOut.cptCode ?? ''}
          </TableCell>
          <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
            {billOut.costSource != undefined ? CostSourceText[billOut.costSource!] : ''}
            {billOut.otherDescription ? (
              <Tooltip title={billOut.otherDescription}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell
            style={{ paddingRight: 0 }}
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            align="right"
          >
            {formatCurrency((billOut.actualCost ?? 0).toString())}
          </TableCell>
          <TableCell
            style={{ paddingRight: 0 }}
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            align="right"
          >
            {formatCurrency((billOut.cost ?? 0).toString())}
          </TableCell>
          <TableCell style={{ paddingLeft: 5 }} align="left">
            {billOut.calculationExplanation ? (
              <Tooltip title={billOut.calculationExplanation}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          {this.state.viewOnly ||
          billOut.originalQuoteId == this.props.selectedQuote!.id ||
          (this.props.selectedQuote?.isConsumer &&
            billOut.movedToQuoteLegacyEOCGroupNumber &&
            !this.props.selectedQuote?.hasBeenConsumed) ? (
            <></>
          ) : billOut.markedDeleted ? (
            <TableCell align="right">
              <UndoRounded
                onClick={() =>
                  this.props.deleteLineItem!(
                    QuoteLineItemType.AN,
                    index,
                    false,
                    this.forceUpdateHandler
                  )
                }
                cursor="pointer"
              ></UndoRounded>
            </TableCell>
          ) : (
            <TableCell align="right">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Edit
                  onClick={() => this.props.openEditModal!(billOut, index, 'AN')}
                  cursor="pointer"
                  fontSize="small"
                />
                <RemoveCircle
                  onClick={() =>
                    this.props.deleteLineItem!(
                      QuoteLineItemType.AN,
                      index,
                      false,
                      this.forceUpdateHandler
                    )
                  }
                  cursor="pointer"
                  color="error"
                />
              </Box>
            </TableCell>
          )}
        </TableRow>
      ))
    }
  }

  fetchProfessionalLineItems = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}

      GetProfessionalComponentLineItems(quoteId!).then((result) => {
        this.props.selectedQuote!.professionalComponentLineItems! = result
        resolve(result)
      })
    })
  }

  fetchProfessionalClaimButtons = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'PC'

      GetClaimButtonsWithSummary(quoteId!, providerType).then((result) => {
        this.props.selectedQuote!.claimButtonsPC = result.claimButtons
        this.props.selectedQuote!.claimButtonsPCTotals = result.providerTypeSummaries
        this.setState({ buttonsLoading: false })
        resolve(result)
      })
    })
  }

  private mapProfessionalGridData = () => {
    if (this.props.selectedQuote?.professionalComponentLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={15} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.professionalComponentLineItems!.map(
        (billOut, index) => (
          <TableRow key={index} className="gridPadding">
            <TableCell>
              {this.props.selectedQuote?.isConsumer &&
              billOut.movedToQuoteLegacyEOCGroupNumber ? (
                <Tooltip title={`Consumed Line Item`}>
                  <span
                    style={{
                      fontSize: 'large',
                      verticalAlign: 'middle',
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingBottom: '8px',
                      cursor: 'default',
                    }}
                  >
                    🐠&nbsp;&nbsp;
                  </span>
                </Tooltip>
              ) : billOut.originalQuoteId &&
                billOut.originalQuoteId == this.props.selectedQuote!.id ? (
                <Tooltip
                  title={`This line item has been consumed by quote ${billOut.movedToQuoteLegacyEOCGroupNumber}.`}
                >
                  <DriveFileMoveOutlined fontSize="small" color="error" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
              {billOut.providerName}
            </TableCell>
            <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
              {billOut.physicianName}
            </TableCell>
            <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
              {billOut.typeText ?? ''}
            </TableCell>
            <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
              {billOut.cptCode ?? ''}
            </TableCell>
            <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
              {billOut.costSource != undefined ? CostSourceText[billOut.costSource!] : ''}
              {billOut.otherDescription ? (
                <Tooltip title={billOut.otherDescription}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell
              style={{ paddingRight: 0 }}
              className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
              align="right"
            >
              {billOut.cost == 0 && billOut.assistantMultiplier != undefined
                ? 'N/A'
                : formatCurrency((billOut.actualCost ?? 0).toString())}
            </TableCell>
            <TableCell align="left" style={{ paddingLeft: 5 }}>
              {billOut.assistantSurgeonInfo ? (
                <Tooltip title={billOut.assistantSurgeonInfo}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell
              style={{ paddingRight: 0 }}
              className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
              align="right"
            >
              {billOut.cost == 0 &&
              billOut.assistantMultiplier != undefined &&
              billOut.calculationExplanation == undefined
                ? 'Will Calculate on Save'
                : formatCurrency((billOut.cost ?? 0).toString())}
            </TableCell>
            <TableCell align="left" style={{ paddingLeft: 5 }}>
              {billOut.calculationExplanation ? (
                <Tooltip title={billOut.calculationExplanation}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            {this.state.viewOnly ||
            billOut.originalQuoteId == this.props.selectedQuote!.id ||
            (this.props.selectedQuote?.isConsumer &&
              billOut.movedToQuoteLegacyEOCGroupNumber &&
              !this.props.selectedQuote?.hasBeenConsumed) ? (
              <></>
            ) : billOut.markedDeleted ? (
              <TableCell align="right">
                <UndoRounded
                  onClick={() =>
                    this.props.deleteLineItem!(
                      QuoteLineItemType.PC,
                      index,
                      false,
                      this.forceUpdateHandler
                    )
                  }
                  cursor="pointer"
                ></UndoRounded>
              </TableCell>
            ) : (
              <TableCell align="right">
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Edit
                    onClick={() => this.props.openEditModal!(billOut, index, 'PC')}
                    cursor="pointer"
                    fontSize="small"
                  />
                  <RemoveCircle
                    onClick={() =>
                      this.props.deleteLineItem!(
                        QuoteLineItemType.PC,
                        index,
                        false,
                        this.forceUpdateHandler
                      )
                    }
                    cursor="pointer"
                    color="error"
                  />
                </Box>
              </TableCell>
            )}
          </TableRow>
        )
      )
    }
  }

  fetchPhysicalLineItems = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}

      GetPhysicalTherapyLineItems(quoteId!).then((result) => {
        this.props.selectedQuote!.physicalTherapyLineItems! = result
        resolve(result)
      })
    })
  }

  fetchPhysicalClaimButtons = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'PT'

      GetClaimButtonsWithSummary(quoteId!, providerType).then((result) => {
        this.props.selectedQuote!.claimButtonsPT = result.claimButtons
        this.props.selectedQuote!.claimButtonsPTTotals = result.providerTypeSummaries
        this.setState({ buttonsLoading: false })
        resolve(result)
      })
    })
  }

  private mapPhysicalGridData = () => {
    if (this.props.selectedQuote?.physicalTherapyLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={11} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.physicalTherapyLineItems!.map((billOut, index) => (
        <TableRow key={index} className="gridPadding">
          <TableCell>
            {this.props.selectedQuote?.isConsumer &&
            billOut.movedToQuoteLegacyEOCGroupNumber ? (
              <Tooltip title={`Consumed Line Item`}>
                <span
                  style={{
                    fontSize: 'large',
                    verticalAlign: 'middle',
                    display: 'inline-flex',
                    alignItems: 'center',
                    paddingBottom: '8px',
                    cursor: 'default',
                  }}
                >
                  🐠&nbsp;&nbsp;
                </span>
              </Tooltip>
            ) : billOut.originalQuoteId &&
              billOut.originalQuoteId == this.props.selectedQuote!.id ? (
              <Tooltip
                title={`This line item has been consumed by quote ${billOut.movedToQuoteLegacyEOCGroupNumber}.`}
              >
                <DriveFileMoveOutlined fontSize="small" color="error" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            style={{ whiteSpace: 'nowrap' }}
          >
            {billOut.providerName}
          </TableCell>
          <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
            {billOut.numberOfVisits?.toString()}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            align="right"
          >
            {billOut.actualCostPerVisit
              ? formatCurrency(billOut.actualCostPerVisit?.toString())
              : ''}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            align="right"
          >
            {billOut.actualCostPerVisit && billOut.numberOfVisits
              ? formatCurrency(
                  (billOut.actualCostPerVisit * billOut.numberOfVisits)?.toString()
                )
              : ''}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            align="right"
          >
            {billOut.costPerVisit ? formatCurrency(billOut.costPerVisit?.toString()) : ''}
          </TableCell>
          <TableCell
            style={{ paddingRight: 0 }}
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            align="right"
          >
            {formatCurrency((billOut.cost ?? 0).toString())}
          </TableCell>
          {this.state.viewOnly ||
          billOut.originalQuoteId == this.props.selectedQuote!.id ||
          (this.props.selectedQuote?.isConsumer &&
            billOut.movedToQuoteLegacyEOCGroupNumber &&
            !this.props.selectedQuote?.hasBeenConsumed) ? (
            <></>
          ) : billOut.markedDeleted ? (
            <TableCell align="right">
              <UndoRounded
                onClick={() =>
                  this.props.deleteLineItem!(
                    QuoteLineItemType.PT,
                    index,
                    false,
                    this.forceUpdateHandler
                  )
                }
                cursor="pointer"
              ></UndoRounded>
            </TableCell>
          ) : (
            <TableCell align="right">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Edit
                  onClick={() => this.props.openEditModal!(billOut, index, 'PT')}
                  cursor="pointer"
                  fontSize="small"
                />
                <RemoveCircle
                  onClick={() =>
                    this.props.deleteLineItem!(
                      QuoteLineItemType.PT,
                      index,
                      false,
                      this.forceUpdateHandler
                    )
                  }
                  cursor="pointer"
                  color="error"
                />
              </Box>
            </TableCell>
          )}
        </TableRow>
      ))
    }
  }

  fetchMiscellaneousLineItems = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}

      GetMiscellaneousLineItems(quoteId!).then((result) => {
        this.props.selectedQuote!.miscellaneousLineItems! = result
        resolve(result)
      })
    })
  }

  fetchMiscellaneousClaimButtons = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'MI'

      GetClaimButtonsWithSummary(quoteId!, providerType).then((result) => {
        this.props.selectedQuote!.claimButtonsMI = result.claimButtons
        this.props.selectedQuote!.claimButtonsMITotals = result.providerTypeSummaries
        this.setState({ buttonsLoading: false })
        resolve(result)
      })
    })
  }

  private mapMiscGridData = () => {
    if (this.props.selectedQuote?.miscellaneousLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={20} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.miscellaneousLineItems!.map((billOut, index) => (
        <TableRow key={index} className="gridPadding">
          <TableCell>
            {this.props.selectedQuote?.isConsumer &&
            billOut.movedToQuoteLegacyEOCGroupNumber ? (
              <Tooltip title={`Consumed Line Item`}>
                <span
                  style={{
                    fontSize: 'large',
                    verticalAlign: 'middle',
                    display: 'inline-flex',
                    alignItems: 'center',
                    paddingBottom: '8px',
                    cursor: 'default',
                  }}
                >
                  🐠&nbsp;&nbsp;
                </span>
              </Tooltip>
            ) : billOut.originalQuoteId &&
              billOut.originalQuoteId == this.props.selectedQuote!.id ? (
              <Tooltip
                title={`This line item has been consumed by quote ${billOut.movedToQuoteLegacyEOCGroupNumber}.`}
              >
                <DriveFileMoveOutlined fontSize="small" color="error" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
            {billOut.typeText ?? ''}
          </TableCell>
          <TableCell
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            style={{ whiteSpace: 'nowrap' }}
          >
            {billOut.providerName}
          </TableCell>
          <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
            {billOut.lineDescription ?? ''}
          </TableCell>
          <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
            {billOut.cptCode ?? ''}
          </TableCell>
          <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
            {billOut.costSource != undefined ? CostSourceText[billOut.costSource!] : ''}
            {billOut.otherDescription ? (
              <Tooltip title={billOut.otherDescription}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell
            style={{ paddingRight: 0 }}
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            align="right"
          >
            {formatCurrency((billOut.actualCost ?? 0).toString())}
          </TableCell>
          <TableCell
            style={{ paddingRight: 0 }}
            className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
            align="right"
          >
            {formatCurrency((billOut.cost ?? 0).toString())}
          </TableCell>
          <TableCell style={{ paddingLeft: 5 }} align="left">
            {billOut.calculationExplanation ? (
              <Tooltip title={billOut.calculationExplanation}>
                <InfoOutlined fontSize="small" />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          {this.state.viewOnly ||
          billOut.originalQuoteId == this.props.selectedQuote!.id ||
          (this.props.selectedQuote?.isConsumer &&
            billOut.movedToQuoteLegacyEOCGroupNumber &&
            !this.props.selectedQuote?.hasBeenConsumed) ? (
            <></>
          ) : billOut.markedDeleted ? (
            <TableCell align="right">
              <UndoRounded
                onClick={() =>
                  this.props.deleteLineItem!(
                    QuoteLineItemType.MI,
                    index,
                    false,
                    this.forceUpdateHandler
                  )
                }
                cursor="pointer"
              ></UndoRounded>
            </TableCell>
          ) : (
            <TableCell align="right">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Edit
                  onClick={() => this.props.openEditModal!(billOut, index, 'MI')}
                  cursor="pointer"
                  fontSize="small"
                />
                <RemoveCircle
                  onClick={() =>
                    this.props.deleteLineItem!(
                      QuoteLineItemType.MI,
                      index,
                      false,
                      this.forceUpdateHandler
                    )
                  }
                  cursor="pointer"
                  color="error"
                />
              </Box>
            </TableCell>
          )}
        </TableRow>
      ))
    }
  }

  fetchTechnicalLineItems = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}

      GetTechnicalComponentLineItems(quoteId!).then((result) => {
        this.props.selectedQuote!.technicalComponentLineItems! = result
        resolve(result)
      })
    })
  }

  fetchTechnicalClaimButtons = async () => {
    return new Promise((resolve) => {
      const { quoteId } = this.props.match?.params || {}
      const providerType = 'TC'

      GetClaimButtonsWithSummary(quoteId!, providerType).then((result) => {
        this.props.selectedQuote!.claimButtonsTC = result.claimButtons
        this.props.selectedQuote!.claimButtonsTCTotals = result.providerTypeSummaries
        this.setState({ buttonsLoading: false })
        resolve(result)
      })
    })
  }

  private mapTechnicalGridData = () => {
    if (this.props.selectedQuote?.technicalComponentLineItems!.length == 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={15} align="center">
            Click + to Add a Line Item
          </TableCell>
        </TableRow>
      )
    } else {
      return this.props.selectedQuote?.technicalComponentLineItems!.map(
        (billOut, index) => (
          <TableRow key={index} className="gridPadding">
            <TableCell>
              {this.props.selectedQuote?.isConsumer &&
              billOut.movedToQuoteLegacyEOCGroupNumber ? (
                <Tooltip title={`Consumed Line Item`}>
                  <span
                    style={{
                      fontSize: 'large',
                      verticalAlign: 'middle',
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingBottom: '8px',
                      cursor: 'default',
                    }}
                  >
                    🐠&nbsp;&nbsp;
                  </span>
                </Tooltip>
              ) : billOut.originalQuoteId &&
                billOut.originalQuoteId == this.props.selectedQuote!.id ? (
                <Tooltip
                  title={`This line item has been consumed by quote ${billOut.movedToQuoteLegacyEOCGroupNumber}.`}
                >
                  <DriveFileMoveOutlined fontSize="small" color="error" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell
              className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
              style={{ whiteSpace: 'nowrap' }}
            >
              {billOut.providerName}
            </TableCell>
            <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
              {billOut.typeText ?? ''}
            </TableCell>
            <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
              {billOut.cptCode ?? ''}
            </TableCell>
            <TableCell className={this.getLineItemStyle(billOut.markedDeleted ?? false)}>
              {billOut.costSource != undefined ? CostSourceText[billOut.costSource!] : ''}
              {billOut.otherDescription ? (
                <Tooltip title={billOut.otherDescription}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell
              style={{ paddingRight: 0 }}
              className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
              align="right"
            >
              {formatCurrency((billOut.actualCost ?? 0).toString())}
            </TableCell>
            <TableCell
              style={{ paddingRight: 0 }}
              className={this.getLineItemStyle(billOut.markedDeleted ?? false)}
              align="right"
            >
              {formatCurrency((billOut.cost ?? 0).toString())}
            </TableCell>
            <TableCell style={{ paddingLeft: 5 }} align="left">
              {billOut.calculationExplanation ? (
                <Tooltip title={billOut.calculationExplanation}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            {this.state.viewOnly ||
            billOut.originalQuoteId == this.props.selectedQuote!.id ||
            (this.props.selectedQuote?.isConsumer &&
              billOut.movedToQuoteLegacyEOCGroupNumber &&
              !this.props.selectedQuote?.hasBeenConsumed) ? (
              <></>
            ) : billOut.markedDeleted ? (
              <TableCell align="right">
                <UndoRounded
                  onClick={() =>
                    this.props.deleteLineItem!(
                      QuoteLineItemType.TC,
                      index,
                      false,
                      this.forceUpdateHandler
                    )
                  }
                  cursor="pointer"
                ></UndoRounded>
              </TableCell>
            ) : (
              <TableCell align="right">
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Edit
                    onClick={() => this.props.openEditModal!(billOut, index, 'TC')}
                    cursor="pointer"
                    fontSize="small"
                  />
                  <RemoveCircle
                    onClick={() =>
                      this.props.deleteLineItem!(
                        QuoteLineItemType.TC,
                        index,
                        false,
                        this.forceUpdateHandler
                      )
                    }
                    cursor="pointer"
                    color="error"
                  />
                </Box>
              </TableCell>
            )}
          </TableRow>
        )
      )
    }
  }

  private renderPC() {
    return (
      <Accordion
        expanded={this.state.expandProfessional}
        onChange={() =>
          this.setState({ expandProfessional: !this.state.expandProfessional })
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography display="inline" color="textSecondary" variant="body2">
              {this.props.selectedQuote?.claimButtonsPCTotals &&
              this.props.selectedQuote.claimButtonsPCTotals.length > 0 &&
              this.props.selectedQuote.claimButtonsPCTotals[0].claimCount > 0
                ? `PROFESSIONAL COMPONENTS — Claim Charges: ${formatCurrency(
                    this.props.selectedQuote.claimButtonsPCTotals[0].totalClaimChargeAmount?.toString() ??
                      ''
                  )} (${this.props.selectedQuote.claimButtonsPCTotals[0].claimCount} ${
                    this.props.selectedQuote.claimButtonsPCTotals[0].claimCount === 1
                      ? 'item'
                      : 'items'
                  })`
                : 'PROFESSIONAL COMPONENTS'}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Subtotal: ' +
                  this.props.getSubtotal!(QuoteLineItemType.PC) +
                  ' (' +
                  this.props.selectedQuote?.professionalComponentLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length +
                  (this.props.selectedQuote?.professionalComponentLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('PC')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="gridPaddingDense">
                    <TableCell></TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Physician</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Cost Source</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                        paddingLeft: 0,
                      }}
                    >
                      Billed Charges
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell align="right" colSpan={2} style={{ paddingRight: 0 }}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openModal!(this.props.selectedQuote!, 'PC')
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapProfessionalGridData()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private renderTC() {
    return (
      <Accordion
        expanded={this.state.expandTechnical}
        onChange={() => this.setState({ expandTechnical: !this.state.expandTechnical })}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography display="inline" color="textSecondary" variant="body2">
              {this.props.selectedQuote?.claimButtonsTCTotals &&
              this.props.selectedQuote.claimButtonsTCTotals.length > 0 &&
              this.props.selectedQuote.claimButtonsTCTotals[0].claimCount > 0
                ? `TECHNICAL COMPONENTS — Claim Charges: ${formatCurrency(
                    this.props.selectedQuote.claimButtonsTCTotals[0].totalClaimChargeAmount?.toString() ??
                      ''
                  )} (${this.props.selectedQuote.claimButtonsTCTotals[0].claimCount} ${
                    this.props.selectedQuote.claimButtonsTCTotals[0].claimCount === 1
                      ? 'item'
                      : 'items'
                  })`
                : 'TECHNICAL COMPONENTS'}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Subtotal: ' +
                  this.props.getSubtotal!(QuoteLineItemType.TC) +
                  ' (' +
                  this.props.selectedQuote?.technicalComponentLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length +
                  (this.props.selectedQuote?.technicalComponentLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('TC')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="gridPaddingDense">
                    <TableCell></TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Cost Source</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                        paddingLeft: 0,
                      }}
                    >
                      Billed Charges
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell align="right" colSpan={2} style={{ paddingRight: 0 }}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openModal!(this.props.selectedQuote!, 'TC')
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapTechnicalGridData()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private renderAN() {
    return (
      <Accordion
        expanded={this.state.expandAnesth}
        onChange={() => this.setState({ expandAnesth: !this.state.expandAnesth })}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography display="inline" color="textSecondary" variant="body2">
              {this.props.selectedQuote?.claimButtonsANTotals &&
              this.props.selectedQuote.claimButtonsANTotals.length > 0 &&
              this.props.selectedQuote.claimButtonsANTotals[0].claimCount > 0
                ? `ANESTHESIOLOGIST — Claim Charges: ${formatCurrency(
                    this.props.selectedQuote.claimButtonsANTotals[0].totalClaimChargeAmount?.toString() ??
                      ''
                  )} (${this.props.selectedQuote.claimButtonsANTotals[0].claimCount} ${
                    this.props.selectedQuote.claimButtonsANTotals[0].claimCount === 1
                      ? 'item'
                      : 'items'
                  })`
                : 'ANESTHESIOLOGIST'}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Subtotal: ' +
                  this.props.getSubtotal!(QuoteLineItemType.AN) +
                  ' (' +
                  this.props.selectedQuote?.anesthesiaLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length +
                  (this.props.selectedQuote?.anesthesiaLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('AN')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table" sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow className="gridPaddingDense">
                    <TableCell></TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Physician</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Cost Source</TableCell>
                    <TableCell align="right" style={{ paddingRight: 0 }}>
                      Billed Charges
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell align="right" colSpan={2} style={{ paddingRight: 0 }}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openModal!(this.props.selectedQuote!, 'AN')
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapAnesthesiaGridData()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private renderPT() {
    return (
      <Accordion
        expanded={this.state.expandPhysical}
        onChange={() => this.setState({ expandPhysical: !this.state.expandPhysical })}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <span>
              <Typography display="inline" color="textSecondary" variant="body2">
                {this.props.selectedQuote?.claimButtonsPTTotals &&
                this.props.selectedQuote.claimButtonsPTTotals.length > 0 &&
                this.props.selectedQuote.claimButtonsPTTotals[0].claimCount > 0
                  ? `PHYSICAL THERAPY — Claim Charges: ${formatCurrency(
                      this.props.selectedQuote.claimButtonsPTTotals[0].totalClaimChargeAmount?.toString() ??
                        ''
                    )} (${this.props.selectedQuote.claimButtonsPTTotals[0].claimCount} ${
                      this.props.selectedQuote.claimButtonsPTTotals[0].claimCount === 1
                        ? 'item'
                        : 'items'
                    })`
                  : 'PHYSICAL THERAPY'}
              </Typography>
              {this.props.selectedQuote?.isNoPtToBeScheduled ? (
                <Typography
                  color="primary"
                  variant="caption"
                  style={{ paddingLeft: '8px' }}
                >
                  <strong>No PT to be scheduled for this patient</strong>
                </Typography>
              ) : (
                ''
              )}
            </span>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Subtotal: ' +
                  this.props.getSubtotal!(QuoteLineItemType.PT) +
                  ' (' +
                  this.props.selectedQuote?.physicalTherapyLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length +
                  (this.props.selectedQuote?.physicalTherapyLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('PT')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="gridPadding">
                    <TableCell></TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Number of Visits</TableCell>
                    <TableCell align="right">Est. U&C Per Visit</TableCell>
                    <TableCell align="right">Est. U&C $</TableCell>
                    <TableCell align="right">Quoted Per Visit</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell align="right" colSpan={1} style={{ paddingRight: 0 }}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openModal!(this.props.selectedQuote!, 'PT')
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>
                  {this.mapPhysicalGridData()}
                </TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  private renderMI() {
    return (
      <Accordion
        expanded={this.state.expandMisc}
        onChange={() => this.setState({ expandMisc: !this.state.expandMisc })}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography display="inline" color="textSecondary" variant="body2">
              {this.props.selectedQuote?.claimButtonsMITotals &&
              this.props.selectedQuote.claimButtonsMITotals.length > 0 &&
              this.props.selectedQuote.claimButtonsMITotals[0].claimCount > 0
                ? `MISCELLANEOUS — Claim Charges: ${formatCurrency(
                    this.props.selectedQuote.claimButtonsMITotals[0].totalClaimChargeAmount?.toString() ??
                      ''
                  )} (${this.props.selectedQuote.claimButtonsMITotals[0].claimCount} ${
                    this.props.selectedQuote.claimButtonsMITotals[0].claimCount === 1
                      ? 'item'
                      : 'items'
                  })`
                : 'MISCELLANEOUS'}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
            >
              <span>
                {' Subtotal: ' +
                  this.props.getSubtotal!(QuoteLineItemType.MI) +
                  ' (' +
                  this.props.selectedQuote?.miscellaneousLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length +
                  (this.props.selectedQuote?.miscellaneousLineItems!.filter(
                    (x) => x.originalQuoteId != this.props.selectedQuote!.id
                  ).length == 1
                    ? ' item)'
                    : ' items)')}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="row">
            <Grid item xs={12}>
              <>{this.props.renderClaimButtons!('MI')}</>
            </Grid>
            <Grid item xs={12}>
              <Table aria-label="simple table" sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow className="gridPaddingDense">
                    <TableCell></TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>CPT</TableCell>
                    <TableCell>Cost Source</TableCell>
                    <TableCell align="right" style={{ paddingRight: 0 }}>
                      Billed Charges
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted $
                    </TableCell>
                    <TableCell align="right" colSpan={2} style={{ paddingRight: 0 }}>
                      {this.state.viewOnly ? (
                        <></>
                      ) : (
                        <Fab
                          size="small"
                          color="primary"
                          disabled={this.props.selectedQuote == undefined}
                          aria-label="Add"
                          onClick={() =>
                            this.props.openModal!(this.props.selectedQuote!, 'MI')
                          }
                        >
                          <Add />
                        </Fab>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '100%' }}>{this.mapMiscGridData()}</TableBody>
              </Table>
            </Grid>
            {!this.state.viewOnly && this.props.renderLegend!()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  public quoteRef = React.createRef<any>()

  public render() {
    const { selectedQuote, getLineItemsTotals } = this.props
    const isLoading = this.state.isLoading
    const isReadyToRender = selectedQuote && !isLoading

    return (
      <>
        {!isReadyToRender ? (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 800,
            }}
          >
            <CircularProgress size={100} />
          </div>
        ) : (
          <Formik
            innerRef={this.quoteRef}
            initialValues={createIQuoteFormValues(selectedQuote)}
            onSubmit={() => {}}
            validationSchema={QuoteSchema}
            enableReinitialize={true}
          >
            {({ values, dirty, submitCount }) => (
              <>
                <AddQuoteLineModal reloadParent={this.forceUpdateHandler} />
                <EditQuoteLineModal reloadParent={this.forceUpdateHandler} />
                <Form>
                  {
                    //@ts-ignore
                    values.adjustedCost === '' || values.targetMargin === '' ? (
                      <Grid item container justifyContent="flex-end">
                        <Typography
                          style={{
                            paddingRight: '10px',
                            paddingBottom: '10px',
                          }}
                          color="error"
                        >
                          Missing Quote Total Data
                        </Typography>
                      </Grid>
                    ) : (
                      <></>
                    )
                  }
                  <Grid container direction="column" spacing={2} wrap="nowrap">
                    <Grid item>
                      {this.props.selectedQuote && this.props.quoteInboxItem && (
                        <QuoteDetails
                          disableInvoiceButtons={
                            (dirty || this.state.lineItemDirty) && submitCount === 0
                          }
                          values={values}
                          selectedQuote={this.props.selectedQuote}
                          assistantWarning={this.props.assistantWarning}
                          viewMode={this.state.viewOnly}
                          updateMain={this.forceUpdateHandler}
                          isAdjusterEmailModalOpen={this.props.isAdjusterEmailModalOpen}
                          rerouteToPath={this.props.rerouteToPath}
                          takeoverOfCareDate={this.props.quoteInboxItem.takeoverOfCare}
                          quoteInboxItem={this.props.quoteInboxItem}
                          isReferralContactModalOpen={
                            this.props.isReferralContactModalOpen
                          }
                          openReferralContactModal={this.props.openReferralContactModal}
                          openBundleClaimsModal={this.props.openBundleClaimsModal}
                          excludeAllLineItems={this.props.excludeAllLineItems}
                          deleteAllLineItems={this.props.deleteAllLineItems}
                        />
                      )}
                    </Grid>
                    <Grid item>{this.renderPC()}</Grid>
                    <Grid item>{this.renderTC()}</Grid>
                    <Grid item>{this.renderAN()}</Grid>
                    <Grid item>{this.renderPT()}</Grid>
                    <Grid item>{this.renderMI()}</Grid>
                    <Grid container item direction="row" spacing={2}>
                      <Grid item xs={8}>
                        <CardWithTitle title="Notes" icon={Assignment}>
                          <FullWidthField
                            variant="standard"
                            disabled={this.state.viewOnly}
                            name="note"
                            fullWidth
                            multiline
                            rows="9"
                          />
                        </CardWithTitle>
                      </Grid>
                      <QuoteTotals
                        viewMode={this.state.viewOnly}
                        reconMode={false}
                        getLineItemsTotal={getLineItemsTotals!}
                        isConsumerQuote={this.props.selectedQuote?.isConsumer}
                        hasBeenConsumed={this.props.selectedQuote?.hasBeenConsumed}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </>
            )}
          </Formik>
        )}
      </>
    )
  }
}

const InjectedquotesList = inject<
  IStores,
  ICreateQuoteProps,
  Partial<ICreateQuoteProps>,
  any
>((stores: IStores) => ({
  getLineItemsTotals: stores.quotes.getLineItemsTotal,
  getProviderTypes: stores.quotes.getProviderTypes,
  getTypesByCategory: stores.quotes.getTypesByCategory,
  loadQuote: stores.quotes.loadQuote,
  providerTypes: stores.quotes.providerTypes,
  rerouteToPath: stores.global.rerouteToPath,
  selectedQuote: stores.quotes.selectedQuote,
  openModal: stores.quotes.openQuoteLineModal,
  openEditModal: stores.quotes.openEditQuoteLineModal,
  assistantWarning: stores.quotes.assistantWarning,
  isAdjusterEmailModalOpen: stores.quotes.isAdjusterEmailModalOpen,
  isNum: stores.quotes.isNum,
  deleteLineItem: stores.quotes.deleteLineItem,
  getSubtotal: stores.quotes.getSubtotal,
  renderLegend: stores.quotes.renderLegend,
  setGetQuoteValuesForStaticAction: stores.quotes.setGetQuoteValuesForStaticAction,
  renderClaimButtons: stores.quotes.renderClaimButtons,
  openReferralContactModal: stores.patientEdit.openReferralContactModal,
  openBundleClaimsModal: stores.quotes.openBillsDialogForSelectedQuote,
  isReferralContactModalOpen: stores.patientEdit.isReferralContactModalOpen,
}))(CreateQuote)

// Wrapper component to provide router props to class component
const CreateQuoteWithRouter = (props: any) => {
  const params = useParams<{ quoteId?: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <InjectedquotesList
      {...props}
      match={{ params }}
      history={{ push: navigate }}
      location={location}
    />
  )
}

export default CreateQuoteWithRouter
